/**
 * Responsibility: Implement item status behavior for the ui/helpers surface.
 * Scope: Owns ui/helpers concerns only; cross-surface orchestration is excluded.
 */
type ItemLocation = 'inventory' | 'room'

const DAMAGED_ITEM_RE = /\b(broken|cracked|damaged|shattered|rusted|frayed|torn|depleted|spent|dull)\b/i
const STORED_ITEM_RE = /\b(shelf|stash|stashed|stowed|holster|scabbard|backpack|satchel|crate|locker)\b/i
const ON_HAND_ITEM_RE = /\b(in hand|main hand|off hand|worn|equipped)\b/i

export type ItemStatusInfo = {
  symbols: string[]
  summary: string
  detail: string
}

export function getItemStatusInfo(name: string, location: ItemLocation, equipped = false): ItemStatusInfo {
  const label = String(name ?? '').trim()
  const isDamaged = DAMAGED_ITEM_RE.test(label)
  const isStored = STORED_ITEM_RE.test(label)
  const isOnHand = equipped || ON_HAND_ITEM_RE.test(label)

  const symbols: string[] = []
  const detail: string[] = []

  if (location === 'inventory') {
    if (isOnHand) {
      symbols.push('✋')
      detail.push('Equipped in hand.')
    } else if (isStored) {
      symbols.push('🗄')
      detail.push('Stored in your pack or on a shelf.')
    } else {
      symbols.push('🎒')
      detail.push('Carried in your inventory.')
    }
  } else {
    symbols.push(isStored ? '🗄' : '📍')
    detail.push(isStored ? 'Stored in this location.' : 'Present in this room.')
  }

  if (isDamaged) {
    symbols.push('⚠')
    detail.push('Condition appears damaged or worn.')
  }

  return {
    symbols,
    summary: symbols.join(' '),
    detail: detail.join(' '),
  }
}
